import { Dispatch } from 'react-redux';

import { StoreState } from '../../../reducers';
import { Action } from '../../../actions';
import {
	UndoRedoState,
	Source,
	UndoRedoTextFieldState,
	UndoRedoMediaState,
	UndoRedoButtonState,
	UndoRedoLinkState,
	UndoRedoEnrichmentState,
	UndoRedoIframeState,
	UndoRedoSliderState,
	ChangeSpreadData,
	UndoRedoChangeSpreadState,
	UndoRedoSpreadBackgroundState,
} from '../reducer/history';
import {
	updateEditableButton,
	updateEditableEnrichment,
	updateEditableIframe,
	updateEditableLink,
	updateEditableMedia,
	updateEditableTextField,
	updateEditableSlider,
} from './editableItems';
import { setSpreadBackground, saveSpreadBackgroundPages } from './spreadBackground';
import { changePageSpread } from './pagesController';
import { BackgroundMediaData, BgStyleData, EditorState } from '../reducer';
import Utils from '../../../utils';
import { IndesignTextFieldsConfig } from '../utils/indesignEditor/editableTextFields/getData';
import { HtmlTextFieldsConfig } from '../utils/htmlEditor/editableTextFields/getData';
import { IndesignMediaElementConfig } from '../utils/indesignEditor/editableMedia/getData';
import { HtmlMediaElementConfig } from '../utils/htmlEditor/editableMedia/getData';
import { EnrichmentConfig } from '../utils/indesignEditor/editableEnrichments/getData';
import { IframeConfig } from '../utils/indesignEditor/editableIframes/getData';
import { LinkConfig } from '../utils/editableLinks/getData';
import { ButtonConfig } from '../utils/indesignEditor/editableButtons/getData';
import { SliderConfig } from '../utils/indesignEditor/editableSliders/getData';

type UndoRedoData = IndesignTextFieldsConfig |
	HtmlTextFieldsConfig |
	IndesignMediaElementConfig |
	HtmlMediaElementConfig |
	ButtonConfig |
	LinkConfig |
	EnrichmentConfig |
	IframeConfig |
	SliderConfig |
	ChangeSpreadData |
	BgStyleData;

type SourceDataAndAction = {
	data: UndoRedoData,
	action: () => void,
};

export enum HistoryActionKeys {
	UNDO = '@@editor/history/UNDO',
	REDO = '@@editor/history/REDO',
	ADD_TO_UNDOS = '@@editor/history/ADD_TO_UNDOS',
}

interface UndoAction extends Action {
	type: HistoryActionKeys.UNDO;
	item: UndoRedoState;
}

interface RedoAction extends Action {
	type: HistoryActionKeys.REDO;
	item: UndoRedoState;
}

interface AddToUndosAction extends Action {
	type: HistoryActionKeys.ADD_TO_UNDOS;
	item: UndoRedoState;
}

export type HistoryAction =
	UndoAction |
	RedoAction |
	AddToUndosAction;

const getSourceDataAndAction = (
	undoRedoState: UndoRedoState,
	editorState: EditorState,
	dispatch: Dispatch<StoreState>,
): SourceDataAndAction => {
	switch (undoRedoState.source) {
		case Source.TEXT_FIELD: {
			const { page, id, data } = undoRedoState;
			return {
				data: editorState.editableTextFields[page][id],
				action: () => dispatch(updateEditableTextField(page, id, data, false)),
			};
		}
		case Source.MEDIA: {
			const { page, id, data } = undoRedoState;
			return {
				data: editorState.editableMedia[page][id],
				action: () => dispatch(updateEditableMedia(page, id, data, true, false)),
			};
		}
		case Source.BUTTON: {
			const { page, id, data } = undoRedoState;
			return {
				data: editorState.editableButtons[page][id],
				action: () => dispatch(updateEditableButton(page, id, data, false)),
			};
		}
		case Source.LINK: {
			const { page, id, data } = undoRedoState;
			return {
				data: editorState.editableLinks[page][id],
				action: () => dispatch(updateEditableLink(page, id, data, false)),
			};
		}
		case Source.ENRICHMENT: {
			const { page, id, data } = undoRedoState;
			return {
				data: editorState.editableEnrichments[page][id],
				action: () => dispatch(updateEditableEnrichment(page, id, data, false)),
			};
		}
		case Source.IFRAME: {
			const { page, id, data } = undoRedoState;
			return {
				data: editorState.editableIframes[page][id],
				action: () => dispatch(updateEditableIframe(page, id, data, false)),
			};
		}
		case Source.SLIDER: {
			const { page, id, data } = undoRedoState;
			return {
				data: editorState.editableSliders[page][id],
				action: () => dispatch(updateEditableSlider(page, id, data, true, true, false)),
			};
		}
		case Source.CHANGE_SPREAD: {
			const { data } = undoRedoState;
			return {
				data: { currentSpreadIndex: editorState.pagesController.currentSpreadIndex },
				action: () => dispatch(changePageSpread(data.currentSpreadIndex, false)),
			};
		}
		case Source.SPREAD_BACKGROUND: {
			const { page, data } = undoRedoState;
			return {
				data: editorState.configs.pagesData[page - 1].bgStyle as BgStyleData,
				action: () => {
					const spread = editorState.pagesController.spreads.find(spread => spread.includes(page)) as number[];
					const epubStyle = Utils.isEmptyObject(data)
						? null
						: (data as BgStyleData).epubStyle.backgroundMedia as BackgroundMediaData;
					dispatch(setSpreadBackground(spread, epubStyle, false, true, true, false));
					dispatch(saveSpreadBackgroundPages());
				},
			};
		}
	}
};

const mergeHistoryItem = <StateType extends object, DataType extends object>(item: StateType, currentData: DataType): StateType => ({
	...(item as object),
	data: 'isEditedFromBackoffice' in currentData
		? { ...(currentData as object), isEditedFromBackoffice: true }
		: currentData,
} as StateType);

const getHistoryActionItem = (item: UndoRedoState, currentData: UndoRedoData): UndoRedoState => {
	let castedCurrentData;

	switch (item.source) {
		case Source.TEXT_FIELD: {
			castedCurrentData = currentData;
			return mergeHistoryItem<UndoRedoTextFieldState, IndesignTextFieldsConfig | HtmlTextFieldsConfig>(item, castedCurrentData);
		}
		case Source.MEDIA: {
			castedCurrentData = currentData as IndesignMediaElementConfig | HtmlMediaElementConfig;
			return mergeHistoryItem<UndoRedoMediaState, IndesignTextFieldsConfig | HtmlTextFieldsConfig>(item, castedCurrentData);
		}
		case Source.BUTTON: {
			castedCurrentData = currentData as ButtonConfig;
			return mergeHistoryItem<UndoRedoButtonState, ButtonConfig>(item, castedCurrentData);
		}
		case Source.LINK: {
			castedCurrentData = currentData as LinkConfig;
			return mergeHistoryItem<UndoRedoLinkState, LinkConfig>(item, castedCurrentData);
		}
		case Source.ENRICHMENT: {
			castedCurrentData = currentData as EnrichmentConfig;
			return mergeHistoryItem<UndoRedoEnrichmentState, EnrichmentConfig>(item, castedCurrentData);
		}
		case Source.IFRAME: {
			castedCurrentData = currentData as IframeConfig;
			return mergeHistoryItem<UndoRedoIframeState, IframeConfig>(item, castedCurrentData);
		}
		case Source.SLIDER: {
			castedCurrentData = currentData as SliderConfig;
			return mergeHistoryItem<UndoRedoSliderState, SliderConfig>(item, castedCurrentData);
		}
		case Source.CHANGE_SPREAD: {
			castedCurrentData = currentData as ChangeSpreadData;
			return mergeHistoryItem<UndoRedoChangeSpreadState, ChangeSpreadData>(item, castedCurrentData);
		}
		case Source.SPREAD_BACKGROUND: {
			castedCurrentData = currentData as BgStyleData | {};
			return mergeHistoryItem<UndoRedoSpreadBackgroundState, BgStyleData | {}>(item, castedCurrentData);
		}
	}
};

export const addToUndos = (item: UndoRedoState): AddToUndosAction => ({
	type: HistoryActionKeys.ADD_TO_UNDOS,
	item,
});

export const undo = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState) => {
	const {
		editor,
		editor: { history: { undos } },
	} = getState();

	const lastUndo = undos[undos.length - 1];
	const {
		data: currentData,
		action: undoAction,
	} = getSourceDataAndAction(lastUndo, editor, dispatch);

	const action: UndoAction = {
		type: HistoryActionKeys.UNDO,
		item: getHistoryActionItem(lastUndo, currentData),
	};
	dispatch(action);
	undoAction();
};

export const redo = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState) => {
	const {
		editor,
		editor: { history: { redos } },
	} = getState();

	const firstRedo = redos[0];
	const {
		data: currentData,
		action: redoAction,
	} = getSourceDataAndAction(firstRedo, editor, dispatch);

	const action: RedoAction = {
		type: HistoryActionKeys.REDO,
		item: getHistoryActionItem(firstRedo, currentData),
	};
	dispatch(action);
	redoAction();
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/Editor/actions/history.ts